/**
 * デバッグログユーティリティ
 * 開発環境でのデバッグを支援するための詳細なログ管理
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  component: string;
  action: string;
  data?: any;
  error?: {
    code?: string;
    message: string;
    stack?: string;
  };
  duration?: number; // ミリ秒
}

class DebugLogger {
  private static instance: DebugLogger;
  private logs: LogEntry[] = [];
  private isEnabled: boolean;
  private maxLogs: number = 1000;
  private logToConsole: boolean = true;
  private saveToLocalStorage: boolean = true;
  private localStorageKey: string = 'mederi_debug_logs';

  private constructor() {
    // 開発環境でのみデフォルトで有効
    this.isEnabled = process.env.NODE_ENV === 'development';
    
    // ローカルストレージから設定を読み込み
    const settings = this.loadSettings();
    if (settings) {
      this.isEnabled = settings.enabled ?? this.isEnabled;
      this.logToConsole = settings.logToConsole ?? this.logToConsole;
      this.saveToLocalStorage = settings.saveToLocalStorage ?? this.saveToLocalStorage;
    }

    // ローカルストレージから既存のログを読み込み
    if (this.saveToLocalStorage) {
      this.loadLogs();
    }
  }

  static getInstance(): DebugLogger {
    if (!DebugLogger.instance) {
      DebugLogger.instance = new DebugLogger();
    }
    return DebugLogger.instance;
  }

  private loadSettings(): any {
    try {
      const settings = localStorage.getItem('mederi_debug_settings');
      return settings ? JSON.parse(settings) : null;
    } catch (error) {
      return null;
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('mederi_debug_settings', JSON.stringify({
        enabled: this.isEnabled,
        logToConsole: this.logToConsole,
        saveToLocalStorage: this.saveToLocalStorage
      }));
    } catch (error) {
      console.error('設定の保存に失敗しました:', error);
    }
  }

  private loadLogs(): void {
    try {
      const storedLogs = localStorage.getItem(this.localStorageKey);
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
      }
    } catch (error) {
      console.error('ログの読み込みに失敗しました:', error);
      this.logs = [];
    }
  }

  private saveLogs(): void {
    if (!this.saveToLocalStorage) return;
    
    try {
      // 最新のログのみを保存（maxLogsまで）
      const logsToSave = this.logs.slice(-this.maxLogs);
      localStorage.setItem(this.localStorageKey, JSON.stringify(logsToSave));
    } catch (error) {
      console.error('ログの保存に失敗しました:', error);
    }
  }

  private formatLog(entry: LogEntry): string {
    const parts = [
      `[${entry.timestamp}]`,
      `[${entry.level}]`,
      `[${entry.component}]`,
      `[${entry.action}]`
    ];
    
    if (entry.duration !== undefined) {
      parts.push(`[${entry.duration}ms]`);
    }
    
    const prefix = parts.join(' ');
    
    if (entry.data) {
      return `${prefix} ${JSON.stringify(entry.data, null, 2)}`;
    }
    
    if (entry.error) {
      return `${prefix} ERROR: ${entry.error.message}`;
    }
    
    return prefix;
  }

  private getConsoleMethod(level: LogLevel): typeof console.log {
    switch (level) {
      case 'DEBUG':
        return console.debug;
      case 'INFO':
        return console.info;
      case 'WARN':
        return console.warn;
      case 'ERROR':
        return console.error;
      default:
        return console.log;
    }
  }

  log(
    level: LogLevel,
    component: string,
    action: string,
    data?: any,
    error?: Error | any,
    duration?: number
  ): void {
    if (!this.isEnabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component,
      action,
      data,
      duration
    };

    if (error) {
      entry.error = {
        message: error.message || String(error),
        code: error.code,
        stack: error.stack
      };
    }

    // メモリに追加
    this.logs.push(entry);
    
    // maxLogsを超えたら古いログを削除
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // コンソールに出力
    if (this.logToConsole) {
      const consoleMethod = this.getConsoleMethod(level);
      const formattedLog = this.formatLog(entry);
      
      // レベルごとに色分けして出力
      const styles = {
        DEBUG: 'color: #888; font-weight: normal;',
        INFO: 'color: #2196F3; font-weight: normal;',
        WARN: 'color: #FF9800; font-weight: bold;',
        ERROR: 'color: #F44336; font-weight: bold;'
      };
      
      consoleMethod(`%c${formattedLog}`, styles[level]);
      
      // データやエラーの詳細を展開して表示
      if (entry.data) {
        console.group('📊 データ詳細');
        console.table(entry.data);
        console.groupEnd();
      }
      
      if (entry.error?.stack) {
        console.group('🔥 スタックトレース');
        console.error(entry.error.stack);
        console.groupEnd();
      }
    }

    // ローカルストレージに保存
    this.saveLogs();
  }

  debug(component: string, action: string, data?: any, duration?: number): void {
    this.log('DEBUG', component, action, data, undefined, duration);
  }

  info(component: string, action: string, data?: any, duration?: number): void {
    this.log('INFO', component, action, data, undefined, duration);
  }

  warn(component: string, action: string, data?: any, error?: Error): void {
    this.log('WARN', component, action, data, error);
  }

  error(component: string, action: string, error: Error | any, data?: any): void {
    this.log('ERROR', component, action, data, error);
  }

  // パフォーマンス計測用
  startTimer(component: string, action: string): (data?: any) => void {
    const startTime = performance.now();
    
    return (data?: any) => {
      const duration = Math.round(performance.now() - startTime);
      this.debug(component, action, data, duration);
    };
  }

  // ログをクリア
  clear(): void {
    this.logs = [];
    if (this.saveToLocalStorage) {
      try {
        localStorage.removeItem(this.localStorageKey);
      } catch (error) {
        console.error('ログのクリアに失敗しました:', error);
      }
    }
  }

  // ログをエクスポート
  export(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // ログをCSV形式でエクスポート
  exportAsCSV(): string {
    const headers = ['Timestamp', 'Level', 'Component', 'Action', 'Duration(ms)', 'Data', 'Error'];
    const rows = this.logs.map(log => [
      log.timestamp,
      log.level,
      log.component,
      log.action,
      log.duration || '',
      log.data ? JSON.stringify(log.data) : '',
      log.error ? log.error.message : ''
    ]);
    
    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    return csv;
  }

  // ログをダウンロード
  download(format: 'json' | 'csv' = 'json'): void {
    const content = format === 'csv' ? this.exportAsCSV() : this.export();
    const blob = new Blob([content], { type: format === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mederi_debug_logs_${new Date().toISOString().replace(/[:.]/g, '-')}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // 設定を変更
  configure(options: {
    enabled?: boolean;
    logToConsole?: boolean;
    saveToLocalStorage?: boolean;
  }): void {
    if (options.enabled !== undefined) this.isEnabled = options.enabled;
    if (options.logToConsole !== undefined) this.logToConsole = options.logToConsole;
    if (options.saveToLocalStorage !== undefined) this.saveToLocalStorage = options.saveToLocalStorage;
    
    this.saveSettings();
  }

  // 現在の設定を取得
  getConfiguration(): {
    enabled: boolean;
    logToConsole: boolean;
    saveToLocalStorage: boolean;
    logCount: number;
  } {
    return {
      enabled: this.isEnabled,
      logToConsole: this.logToConsole,
      saveToLocalStorage: this.saveToLocalStorage,
      logCount: this.logs.length
    };
  }

  // 特定のコンポーネントのログのみを取得
  getLogsByComponent(component: string): LogEntry[] {
    return this.logs.filter(log => log.component === component);
  }

  // 特定のレベルのログのみを取得
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  // エラーログのみを取得
  getErrors(): LogEntry[] {
    return this.logs.filter(log => log.level === 'ERROR');
  }
}

// シングルトンインスタンスをエクスポート
export const logger = DebugLogger.getInstance();

// グローバルオブジェクトに登録（ブラウザコンソールから使用可能）
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).mederiDebugLogger = logger;
  console.log(`
🔍 Mederi Debug Logger が利用可能です！
使用方法:
  - mederiDebugLogger.getConfiguration() - 現在の設定を確認
  - mederiDebugLogger.download() - ログをダウンロード
  - mederiDebugLogger.clear() - ログをクリア
  - mederiDebugLogger.configure({enabled: true}) - ログを有効化
  `);
}